import React, {useState, useMemo} from 'react'
import ErrorBanner from '../../components/ErrorBanner'
import LoadingBanner from '../../components/LoadingBanner'

import CreateDocumentModal from '../documents/CreateModal'
import ConfirmDeleteDocumentModal from '../documents/ConfirmDeleteModal'
import EditDocumentModal from '../documents/EditModal'

import Document from '../../models/Document'
import {useProject} from "../../providers/ClientProjectProvider";
import {Link, NavLink, Outlet, useOutletContext, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import {ProjectPositionProvider} from "../../providers/ProjectPositionProvider";
import ProjectFormProvider, {useProjectForm} from "../../providers/ProjectFormProvider";
import TeamCandidateProvider from '../../providers/TeamCandidateProvider'
import {SelectedTeamCandidate} from '../project-bidding/ProjectShortlistedTeamCandidates'
import PositionCostOverview from "../project-bidding/components/PositionCostOverview";
import PositionFormProvider from "../../providers/PositionFormProvider";
import CreateChangeOrderForm from "../client-lead-tools/change-orders/CreateChangeOrderForm";
import {Badge} from "antd";
import {CheckSquareOutlined} from "@ant-design/icons";
import HideTalentPricingTooltip from '../clients/HideTalentPricingTooltip';
import ProjectContractExpensesModal from './components/ProjectContractExpensesModal';
import ClientSatisfactionSurveyModal from './components/ClientSatisfactionSurveyModal';

const ProjectShortlistTab = (props) => {
    const {auth} = useAuth();

    const context = useOutletContext()
    if (!auth.canManageClients()) {
        return null;
    }
    return (
        <Outlet {...props} context={context}/>
    )
}

const ProjectDocumentsTab = (props) => {
    const {project, isLoading, isProjectUpdating: isDisabled, error, refetch} = useProject();

    const navigate = useNavigate();
    const location = useLocation();

    const [selectedDocument, setSelectedDocument] = useState(null);
    const [shownModal, setShownModel] = useState(null);
    const documents = project?.documents || [];


    const createDocument = async (doc) => {
        // console.log('createDocument');

        let newDocument = await Document.uploadForProject(project, doc);
        if (!newDocument) {
            return;
        }

        TB.toast('New document uploaded.', {type: 'success'});
        await setShownModel(null);
        await setSelectedDocument(null)
    }

    const editDocument = async (doc) => {
        // console.log('editDocument', doc);

        await setSelectedDocument(doc)
        await setShownModel('Edit');
    }

    const updateDocument = async (doc) => {
        // console.log('updateDocument', doc);

        let projectDoc = Document.make({...doc});
        let updatedDoc = await projectDoc.updateForProject(
            project
        );
        if (!updatedDoc) {
            return;
        }

        TB.toast('Document updated.', {type: 'success'});
        await setShownModel(null);
        await setSelectedDocument(null)
    }

    const confirmDeleteDocument = async (doc) => {
        // console.log('confirmDeleteDocument', doc);

        await setSelectedDocument(doc)
        await setShownModel('ConfirmDelete');
    }

    const deleteDocument = async (doc) => {
        // console.log('deleteDocument', doc);

        let projectDoc = Document.make({...doc});
        let deletedDoc = await projectDoc.archiveForProject(project);
        if (!deletedDoc) {
            return;
        }

        TB.toast('Document removed.', {type: 'info'});
        await setShownModel(null);
        await setSelectedDocument(null)
    }

    return (
        <React.Fragment>
            {documents?.length && (
                <div
                    className="max-w-screen-md mx-auto px-12 flex flex-col items-stretch justify-start zero-padding-mobile">
                    <div className="flex flex-col items-stretch py-4 mx-auto">
                        <div className="card p-3 mt-4">
                            <div className="mb-2 flex"
                                 style={{justifyContent: 'space-between', alignItems: 'center'}}>

                                <h3 style={{fontSize: '1.3rem', marginBottom: 0}}>Documents</h3>

                                {/*<button type="button"
						className="btn btn-primary"
						onClick={() => this.newDocument()}>
						<i className="fa fa-plus mr-2"/>
						Upload
					</button>*/}
                            </div>

                            <table className="table table-bordered" style={{marginBottom: 0, minWidth: 600}}>
                                <tbody>
                                {documents.map((doc, i) => (
                                    <tr key={`documentRow${i}`}>
                                        <td style={{fontSize: 14, width: '60%'}}>
                                            {doc.display_name}
                                            {doc.type_name !== 'Miscellaneous' && (<br/>)}
                                            {doc.type_name !== 'Miscellaneous' && (
                                                <span className="badge badge-secondary">{doc.type_name}</span>
                                            )}
                                        </td>
                                        <td style={{fontSize: 14}}>
                                            {TB.moment(doc.uploaded_at).format('MMM D YYYY')}
                                        </td>
                                        <td style={{minWidth: 150}}>
                                            <a href={doc.path}
                                               target="_blank"
                                               className="btn btn-outline-primary btn-sm mr-2">
                                                <i className="fa fa-external-link-alt"/>
                                            </a>
                                            {/*<button type="button"
	    								className="btn btn-primary btn-sm mr-2"
	    								onClick={() => this.editDocument(doc)}>
	    								<i className="fa fa-edit"/>
	    							</button>
	    							<button type="button"
	    								className="btn btn-danger btn-sm"
	    								onClick={() => this.confirmDeleteDocument(doc)}>
	    								<i className="fa fa-trash"/>
	    							</button>*/}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDeleteDocumentModal
                isVisible={shownModal === 'ConfirmDelete'}
                document={selectedDocument}
                toggle={shouldShow => setShownModel(shouldShow ? 'ConfirmDelete' : null)}
                onConfirm={deleteDocument}
                isDisabled={isDisabled}/>

            <EditDocumentModal
                isVisible={shownModal === 'Edit'}
                document={selectedDocument}
                toggle={shouldShow => setShownModel(shouldShow ? 'Edit' : null)}
                onUpdate={updateDocument}
                isDisabled={isDisabled}/>

            <CreateDocumentModal
                isVisible={shownModal === 'Create'}
                toggle={shouldShow => setShownModel(shouldShow ? 'Create' : null)}
                onSubmit={createDocument}
                isDisabled={isDisabled}/>

        </React.Fragment>
    )
}

const ProjectOverviewTab = (props) => {
    const {project, isLoading, isProjectUpdating: isDisabled, error, refetch} = useProjectForm();
    const {auth, appropriate} = useAuth();

    const [searchParams, setSearchParams] = useSearchParams();

    const handleClose = () => {
        if (isDisabled) {
            return;
        }
        let params = new URLSearchParams(searchParams);
        params.delete('changeOrder');

        return setSearchParams(params)
        // setShowChangeOrderModal(false);
        // setSelectedPosition(null);
        // setChangeOrderType(null);
    }

    const canManageClients = auth?.canManageClients?.();
    const [publishedCOs, unpublishedCOs] = TB.partition(project?.change_orders || [], order => !!order?.published_at)
    if (!project) return null;

    return (
        <div className="flex flex-col items-stretch gap-6">

            <div className="bg-white mb-10">

                <div className="project-detail-container px-2 lg:px-0 flex flex-col items-stretch">

                    {/* Section 1: LABOR - All items in one row */}
                    <div className="pt-6 px-4 border-b border-gray-300 pb-4">
                        <div className={TB.mapClass("grid gap-3 items-start", {
                            "grid-cols-2 md:grid-cols-3 lg:grid-cols-7": canManageClients,
                            "grid-cols-2 md:grid-cols-5": !canManageClients,
                        })}>
                            <div className="flex items-start pt-1">
                                <p className="text-black font-semibold uppercase text-sm">
                                    LABOR
                                </p>
                            </div>

                            <div className="flex flex-col gap-1">
                                <p className="text-wrippleLightDarkest font-semibold text-sm">
                                    Roles:
                                </p>
                                <p className="font-medium text-sm">
                                    {project.isService() ? project.disciplines?.length : project.positions_count}
                                </p>
                            </div>

                            <div className="flex flex-col gap-1">
                                <p className="text-wrippleLightDarkest font-semibold text-sm">
                                    Timeline:
                                </p>
                                <p className="font-medium text-sm whitespace-normal md:whitespace-nowrap">
                                    {TB.moment(project.anticipated_started_at).format('M/D/YYYY')} - {TB.moment(project.anticipated_ended_at).format('M/D/YYYY')}
                                </p>
                            </div>

                            <div className="flex flex-col gap-1">
                                <p className="text-wrippleLightDarkest font-semibold text-sm">
                                    Original Contract Value:
                                </p>
                                {auth.canViewTalentPricing() ? (
                                    <p className="font-medium text-sm">
                                        {TB.formatCurrency(project.original_contract_value, false)}
                                    </p>
                                ) : (
                                    <p className="font-medium text-sm">
                                        <HideTalentPricingTooltip/>
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col gap-1">
                                <p className="text-wrippleLightDarkest font-semibold text-sm">
                                    Contract Value To Date:
                                </p>
                                {auth.canViewTalentPricing() ? (
                                    <p className="font-medium text-sm">
                                        {TB.formatCurrency(project.contract_value, false)}
                                    </p>
                                ) : (
                                    <p className="font-medium text-sm">
                                        <HideTalentPricingTooltip/>
                                    </p>
                                )}
                            </div>

                            {canManageClients && (
                                <React.Fragment>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-wrippleLightDarkest font-semibold text-sm">
                                            Original Talent Cost:
                                        </p>
                                        <p className="font-medium text-sm">
                                            {TB.formatCurrency(project.original_talent_cost, false)}
                                        </p>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <p className="text-wrippleLightDarkest font-semibold text-sm">
                                            Talent Cost To Date:
                                        </p>
                                        <p className="font-medium text-sm">
                                            {TB.formatCurrency(project.talent_cost, false)}
                                        </p>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>

                    {/* Section 2: PROJECT EXPENSES and PROJECT FEES - Same row */}
                    <div className="px-4 border-b border-gray-300 py-4">
                        <div className={TB.mapClass("grid gap-3 items-start", {
                            "grid-cols-2 md:grid-cols-3 lg:grid-cols-7": canManageClients,
                            "grid-cols-2 md:grid-cols-5": !canManageClients,
                        })}>
                            {/* PROJECT EXPENSES */}
                            <div className={TB.mapClass("grid gap-3 items-start flex-col md:flex-row", {
                                "grid-cols-3 col-span-3": canManageClients,
                                "grid-cols-2 col-span-2": !canManageClients,
                            })}>
                                <p className="text-black font-semibold uppercase text-sm whitespace-nowrap">
                                    PROJECT{canManageClients ? <br/> : ' '}EXPENSES
                                </p>
                                {canManageClients ? (
                                    <React.Fragment>
                                        <div className="flex flex-col gap-1">
                                            <p className="text-wrippleLightDarkest font-semibold text-sm whitespace-nowrap">
                                                Client Billable:
                                            </p>
                                            {auth.canViewTalentPricing() ? (
                                                <p className="font-medium text-sm">
                                                    {TB.formatCurrency(project.totalBillableProjectExpenses(), false)}
                                                </p>
                                            ) : (
                                                <p className="font-medium text-sm">
                                                    <HideTalentPricingTooltip/>
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <p className="text-wrippleLightDarkest font-semibold text-sm whitespace-nowrap">
                                                Non-Billable:
                                            </p>
                                            {auth.canViewTalentPricing() ? (
                                                <p className="font-medium text-sm">
                                                    {TB.formatCurrency(project.totalNonBillableProjectExpenses(), false)}
                                                </p>
                                            ) : (
                                                <p className="font-medium text-sm">
                                                    <HideTalentPricingTooltip/>
                                                </p>
                                            )}
                                        </div>
                                    </React.Fragment>
                                ) : (
                                    <div className="flex flex-col gap-1">
                                        <p className="text-wrippleLightDarkest font-semibold text-sm whitespace-nowrap">
                                            Total:
                                        </p>
                                        {auth.canViewTalentPricing() ? (
                                            <p className="font-medium text-sm">
                                                {TB.formatCurrency((project.totalBillableProjectExpenses() || 0), false)}
                                            </p>
                                        ) : (
                                            <p className="font-medium text-sm">
                                                <HideTalentPricingTooltip/>
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* PROJECT FEES */}
                            <div className={TB.mapClass("lg:border-l lg:border-gray-300 pl-3 -ml-3 grid gap-3 items-start flex-col md:flex-row", {
                                "grid-cols-3 col-span-3": canManageClients,
                                "grid-cols-2 col-span-2": !canManageClients,
                            })}>
                                <p className="text-black font-semibold uppercase text-sm whitespace-nowrap">
                                    PROJECT{canManageClients ? <br/> : ' '}FEES
                                </p>
                                {canManageClients ? (
                                    <React.Fragment>
                                        <div className="flex flex-col gap-1">
                                            <p className="text-wrippleLightDarkest font-semibold text-sm whitespace-nowrap">
                                                Client Billable:
                                            </p>
                                            {auth.canViewTalentPricing() ? (
                                                <p className="font-medium text-sm">
                                                    {TB.formatCurrency(project.totalBillableProjectFees() || 0, false)}
                                                </p>
                                            ) : (
                                                <p className="font-medium text-sm">
                                                    <HideTalentPricingTooltip/>
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <p className="text-wrippleLightDarkest font-semibold text-sm whitespace-nowrap">
                                                Internal Cost:
                                            </p>
                                            {auth.canViewTalentPricing() ? (
                                                <p className="font-medium text-sm">
                                                    {TB.formatCurrency(project.totalInternalCostProjectFees() || 0, false)}
                                                </p>
                                            ) : (
                                                <p className="font-medium text-sm">
                                                    <HideTalentPricingTooltip/>
                                                </p>
                                            )}
                                        </div>
                                    </React.Fragment>
                                ) : (
                                    <div className="flex flex-col gap-1">
                                        <p className="text-wrippleLightDarkest font-semibold text-sm whitespace-nowrap">
                                            Total:
                                        </p>
                                        {auth.canViewTalentPricing() ? (
                                            <p className="font-medium text-sm">
                                                {TB.formatCurrency((project.totalBillableProjectFees() || 0), false)}
                                            </p>
                                        ) : (
                                            <p className="font-medium text-sm">
                                                <HideTalentPricingTooltip/>
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Edit Button */}
                            <div className="flex items-end lg:items-start col-span-1">
                                <button
                                    disabled={isDisabled}
                                    onClick={() => {
                                        let params = new URLSearchParams(searchParams);
                                        params.set('showExpenseModal', '1');
                                        return setSearchParams(params);
                                    }}
                                    className="whitespace-nowrap btn btn-link-primary p-0 text-xs">
                                    {canManageClients ? 'Edit Expenses & Fees' : 'View Expenses & Fees'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Total Client Contract Value with breakdown and Change Orders */}
                    <div className="bg-slate-100 px-4 md:px-6 py-4">
                        {/* Total Client Contract Value */}
                        <div className="flex flex-row flex-wrap gap-4 items-start md:items-center justify-between">
                            <div className="flex flex-row gap-3 md:gap-6 flex-grow items-start md:items-center">
                                <div className="flex flex-row items-center self-stretch gap-3 border-r border-gray-500 pr-3">
                                    <p className="text-black font-bold text-xl whitespace-nowrap">
                                        Total Client Contract Value:
                                    </p>
                                    {auth.canViewTalentPricing() ? (
                                        <p className="font-bold text-xl">
                                            {TB.formatCurrency((project.contract_value || 0) + (project.totalBillableProjectExpenses() || 0) + (project.totalBillableProjectFees() || 0), false)}
                                        </p>
                                    ) : (
                                        <p className="font-bold text-xl">
                                            <HideTalentPricingTooltip/>
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-row gap-4 items-center">
                                    <div className="flex flex-col gap-1">
                                        <p className="text-wrippleLightDarkest font-semibold text-base whitespace-nowrap">
                                            Labor:
                                        </p>
                                        {auth.canViewTalentPricing() ? (
                                            <p className="font-semibold text-lg">
                                                {TB.formatCurrency(project.contract_value || 0, false)}
                                            </p>
                                        ) : (
                                            <p className="font-semibold text-lg">
                                                <HideTalentPricingTooltip/>
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-wrippleLightDarkest font-semibold text-base whitespace-nowrap">
                                            Expenses:
                                        </p>
                                        {auth.canViewTalentPricing() ? (
                                            <p className="font-semibold text-lg">
                                                {TB.formatCurrency(project.totalBillableProjectExpenses() || 0, false)}
                                            </p>
                                        ) : (
                                            <p className="font-semibold text-lg">
                                                <HideTalentPricingTooltip/>
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-wrippleLightDarkest font-semibold text-base whitespace-nowrap">
                                            Fees:
                                        </p>
                                        {auth.canViewTalentPricing() ? (
                                            <p className="font-semibold text-lg">
                                                {TB.formatCurrency(project.totalBillableProjectFees() || 0, false)}
                                            </p>
                                        ) : (
                                            <p className="font-semibold text-lg">
                                                <HideTalentPricingTooltip/>
                                            </p>
                                        )}
                                    </div>
                                </div>

                            </div>
                            {canManageClients && (
                                <p className="flex-0 mx-auto text-xs italic font-medium max-w-40">
                                    * note: client will only see labor and billable expenses/fees.
                                </p>
                            )}
                            {/* Change Orders Section */}
                            <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between ml-0 pl-0 lg:-ml-3 lg:pl-3 border-l-0 lg:border-l border-gray-300">
                                <div className="flex flex-col bg-white p-2 gap-1">
                                    <p className="text-wrippleLightDarkest font-semibold text-sm">
                                        Change Orders:
                                    </p>
                                    <p className="font-medium text-sm">
                                        {publishedCOs?.length || 0} Published, <span
                                        className={unpublishedCOs.length ? 'text-danger' : ''}>{unpublishedCOs?.length || 0} Pending</span>
                                    </p>
                                </div>
                                {canManageClients && project.isTimeMaterials() && (
                                    <button
                                        disabled={isDisabled}
                                        onClick={async () => {
                                            if (TB.find(project.change_orders || [], ['published_at', null])) {
                                                return TB.alert("alert", "Existing Change Order Not Published", "There is already a change order that hasn't been published. Edit and publish the existing change order in the Contract History tab before creating a new one", [{title: 'Ok'}])
                                            }

                                            let params = new URLSearchParams(searchParams);
                                            params.set('changeOrder', 'new');
                                            return setSearchParams(params);
                                        }}
                                        className={TB.mapClass("whitespace-nowrap btn btn-primary px-3 py-2 text-sm", {
                                            "disabled-edit-btn": TB.find(project.change_orders || [], [
                                                'published_at',
                                                null
                                            ])
                                        })}>
                                        Create Change Order
                                    </button>
                                )}
                                <ClientSatisfactionSurveyModal/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/*<div>*/}
            {/*    <ClientSatisfactionSurveyModal/>*/}
            {/*</div>*/}
            <div className="w-full px-4 pb-6 mx-auto max-w-[1250px]">
                {!isLoading && project && project.isService() && (
                    <TeamCandidateProvider team={project.assigned_team_candidate}>
                        <SelectedTeamCandidate active/>
                    </TeamCandidateProvider>
                )}

                {!isLoading && project && project.positions?.length > 0 && TB.map(project.positions, (position, index) => {
                    const displayIndex = index + 1;
                    return (
                        <ProjectPositionProvider key={`ppr-${position.id}`} positionId={position.id}>
                            <div className="shortlisted-candidates-card container mx-auto max-w-[1225px]">

                                <div className="role-header flex-row items-stretch justify-between">
                                    <div className="flex flex-col gap-4 items-start flex-grow">
                                        <div className="flex flex-row items-center justify-start">
                                            <h6>
                                                Role {displayIndex} of {project?.sortedPositions()?.length}
                                            </h6>

                                        </div>
                                        <div className="flex flex-row items-center self-stretch">
                                            <h3>{position.title}</h3>
                                        </div>
                                    </div>

                                </div>
                                <PositionFormProvider positionId={position.id}>
                                    <PositionCostOverview
                                        position={position}
                                        canEdit={false}
                                        isDisabled={isDisabled}/>
                                </PositionFormProvider>

                            </div>
                            {displayIndex < project.positions.length && <hr className="mx-9"/>}

                        </ProjectPositionProvider>
                    )
                })}
            </div>

            {searchParams.has('changeOrder') && !isLoading && project && <CreateChangeOrderForm/>}

            <ProjectContractExpensesModal
                visible={searchParams.get('showExpenseModal') === '1'}
                onClose={() => {
                    let params = new URLSearchParams(searchParams);
                    params.delete('showExpenseModal');
                    setSearchParams(params);
                }}
            />
        </div>
    )
}
const ProjectContractHistoryTab = (props) => {
    const {auth} = useAuth();
    const {project, isLoading, isDisabled} = useProjectForm();
    const [searchParams, setSearchParams] = useSearchParams();

    const changeOrders = useMemo(() => {
        // if (!auth.canManageClients()) {
        //     return TB.filter(project?.change_orders || [], order => !!order.published_at)
        // }
        return project?.change_orders || [];
    }, [isDisabled, project?.change_orders, auth]);


    if (!project) return null;
    return (
        <div className="flex flex-col items-stretch gap-6 py-8">
            <div className="container mx-auto max-w-[1225px]">

                <div className="bg-white px-4 py-6 mb-10">
                    <h3 style={{fontSize: '1.3rem', marginBottom: 15}}>Contract History</h3>

                    <table className="table">
                        <thead>
                        <tr>
                            <th className="border-0 text-left"/>
                            <th className="border-0 text-left text-xs">Effective Date</th>
                            <th className="border-0 text-left text-xs">Notes</th>
                            <th className="border-0 text-left text-xs">Resources Affected</th>
                            <th className="border-0 text-left text-xs">Total Hours</th>
                            {auth.canViewTalentPricing() && (
                                <th className="border-0 text-left text-xs">Contract Value</th>
                            )}
                            {auth.isClientConsultant() && (
                                <th className="border-0 text-left text-xs">Resource Cost</th>
                            )}
                            <th className="border-0 text-left"/>
                        </tr>
                        </thead>

                        <tbody className="table-bordered">
                        <tr>
                            <td className="border-0 flex justify-between items-center">
                                <span className="text-xs">
                                    Original Contract
                                </span>
                            </td>
                            <td>
                                <span className="text-xs">
                                    {TB.moment(project.started_at).format('M/D/YYYY')}
                                </span>
                            </td>
                            <td></td>
                            <td>
                                <span className="text-xs">
                                    {project.positions?.length}
                                </span>
                            </td>
                            <td>
                                <span className="text-xs">
                                    {TB.sumBy(project.positions || [], 'original_total_periods')}
                                </span>
                            </td>
                            {auth.canViewTalentPricing() && (
                                <td>
                                    <span className="text-xs">
                                        ${TB.formatNumber(project.original_contract_value, 2)}
                                    </span>
                                </td>
                            )}
                            {auth.isClientConsultant() && (
                                <td>
                                    <span className="text-xs">
                                        ${TB.formatNumber(project.original_talent_cost, 2)}
                                    </span>
                                </td>
                            )}

                            <td className="max-w-[100px]"/>
                        </tr>

                        {changeOrders?.length > 0 && TB.map(TB.orderBy(changeOrders, 'id', 'asc'), (co, i) => {
                            const hourImpact = TB.sumBy(co.items, item => item.new_total_periods - item.original_total_periods);
                            const resourceCostImpact = TB.sumBy(co.items, 'net_talent_cost');
                            const contractValueImpact = TB.sumBy(co.items, 'net_contract_value');
                            return (
                                <tr key={co.id} className={co.published_at ? "bg-gray-50" : "bg-red-50"}>
                                    <td>
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs">
                                                {co.name}
                                            </span>
                                        </div>
                                    </td>

                                    <td>
                                        <span className="text-xs">
                                            {co.published_at ? TB.moment(co.started_at).format('M/D/YYYY') : (
                                                <Badge color={TB.config('color.danger')} count={'Not Published'}/>
                                            )}
                                        </span>
                                    </td>
                                    <td className="max-w-[280px]">
                                        <p className="text-xxs line-clamp-3 text-ellipsis overflow-hidden">
                                            {co.notes || <i className="opacity-60">No Notes Provided</i>}
                                        </p>
                                    </td>
                                    <td>
                                        <span className="text-xs">
                                            {co.items?.length || 0}
                                        </span>
                                    </td>
                                    <td>
                                        <p className={hourImpact !== 0 ? 'font-semibold' : ''}>
                                            <span className="text-xs">
                                                {hourImpact >= 0 ? '+' : '-'}
                                                {Math.abs(hourImpact)}
                                            </span>
                                        </p>
                                    </td>
                                    {auth.canViewTalentPricing() && (
                                        <td>
                                            <p className={contractValueImpact !== 0 ? 'font-semibold' : ''}>
                                                <span className="text-xs">
                                                    {contractValueImpact >= 0 ? '+' : '-'}
                                                    ${TB.formatNumber(Math.abs(contractValueImpact), 2)}
                                                </span>
                                            </p>
                                        </td>
                                    )}

                                    {auth.isClientConsultant() && (
                                        <td>
                                            <p className={resourceCostImpact !== 0 ? 'font-semibold' : ''}>
                                                <span className="text-xs">
                                                    {resourceCostImpact >= 0 ? '+' : '-'}
                                                    ${TB.formatNumber(Math.abs(resourceCostImpact), 2)}
                                                </span>
                                            </p>
                                        </td>
                                    )}

                                    <td className="max-w-[100px]">
                                        {co.published_at ? (
                                            <button type="button" onClick={() => {
                                                let params = new URLSearchParams(searchParams);

                                                params.set('changeOrder', co.id);

                                                return setSearchParams(params);
                                            }} className="btn btn-link-primary p-0 mx-auto text-xs">
                                                View
                                            </button>
                                        ) : (
                                            <button type="button" onClick={() => {
                                                // if (auth.canManageClients()) {
                                                let params = new URLSearchParams(searchParams);

                                                params.set('changeOrder', co.id);

                                                return setSearchParams(params);
                                                // }
                                            }} className="btn btn-link-primary p-0 mx-auto text-xs">
                                                {auth.canManageClients() ? 'Edit' : 'View'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                        <tr className="bg-[#595959] text-white">

                            <td className="border-0 flex justify-between items-center">
                                <span className="text-xs">
                                    Running Total
                                </span>
                            </td>
                            <td>
                                <span className="text-xs">
                                    {/*{TB.moment(project.started_at).format('M/D/YYYY')}*/}
                                </span>
                            </td>
                            <td/>
                            <td>
                                <span className="text-xs">
                                    {project.positions?.length}
                                </span>
                            </td>
                            <td>
                                <span className="text-xs">
                                    {TB.sumBy(project.positions || [], 'actual_total_periods')}
                                </span>
                            </td>
                            {auth.canViewTalentPricing() && (
                                <td>
                                    <span className="text-xs">
                                        ${TB.formatNumber(project.contract_value, 2)}
                                    </span>
                                </td>
                            )}
                            {auth.isClientConsultant() && (
                                <td>
                                    <span className="text-xs">
                                        ${TB.formatNumber(project.talent_cost, 2)}
                                    </span>
                                </td>
                            )}

                            <td className="max-w-[100px]"/>
                        </tr>
                        </tbody>
                    </table>

                    {/*<ModalHeadless isVisible={isModalOpen} onCloseAction={() => setIsModalOpen(false)} closeOnBackdrop>*/}
                    {/*    {selectedChangeOrder && (*/}
                    {/*        <ChangeOrderViewModal*/}
                    {/*            changeOrder={selectedChangeOrder}*/}
                    {/*            onClose={() => setIsModalOpen(false)}*/}
                    {/*        />*/}
                    {/*    )}*/}
                    {/*</ModalHeadless>*/}
                </div>
            </div>

            {searchParams.has('changeOrder') && !isLoading && project && <CreateChangeOrderForm/>}
        </div>
    )
}

const ProjectDetail = (props) => {
    const {project, isLoading, isProjectUpdating: isDisabled, error, refetch, postUpdate, markProjectAsComplete} = useProject();
    const {auth, appropriate} = useAuth();
    const navigate = useNavigate();

    const location = useLocation();
    if (isLoading) {
        return (
            <div className="flex flex-col items-center py-44">
                <LoadingBanner message="Loading Project"/>
            </div>
        )
    }

    const renderBackBtn = () => {

        let back = {
            title: 'Projects',
            path: '/client/projects/active',
        }

        let btnStyles = {
            alignSelf: 'center',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            padding: '0 16px 10px 0',
            // padding: 0,
            color: '#F5F6F8',
            maxWidth: '100%'
        };

        if (TB.isObject(back)
            && (TB.isString(back.path) || TB.isObject(back.path))) {
            return (
                <Link to={back.path}
                      style={btnStyles}>
                    <i className="fa fa-arrow-left mr-2"/>
                    {back.title || 'Back'}
                </Link>
            );
        }


        if ((TB.isObject(back) && !back.path) || (TB.isBoolean(back) && back)) {
            return (
                <button type="button"
                        className="btn btn-link"
                        style={btnStyles}
                        onClick={() => navigate(-1)}>
                    <i className="fa fa-arrow-left mr-2"/>
                    {back.title || 'Back'}
                </button>
            );
        }


        return <div/>;
    }
    return (
        <div className="flex flex-col items-stretch ">
            {!location.pathname.includes('talent-profiles') && (
                <div className="nav-container">
                    <div className="nav-info">

                        {renderBackBtn()}

                        <div className="flex-grow flex-2 flex flex-col items-center gap-3">
                            <div className="flex flex-row items-center gap-6 mx-auto text-white">
                                <h2 className="inline font-semibold text-lg mb-0 text-white whitespace-nowrap">
                                    {project?.name}
                                </h2>



                            </div>
                            <div className="self-stretch flex flex-row gap-4 items-center justify-around">
                                <NavLink

                                    className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                    to="brief">
                                    Brief
                                </NavLink>
                                <NavLink

                                    className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                    to="overview">
                                    Overview
                                </NavLink>
                                {project?.isTimeMaterials() && (
                                    <NavLink
                                        className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                        to="contract-history">
                                        Contract History
                                    </NavLink>
                                )}
                                {auth.canManageClients() && (
                                    <NavLink
                                        className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                        to="short-list">
                                        Original Shortlist
                                    </NavLink>
                                )}
                                <NavLink
                                    className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                    to="documents">
                                    Documents
                                </NavLink>
                            </div>
                        </div>
                        <div className="flex-1 flex items-center justify-end">
                            {auth?.canManageClients() && !project.isComplete() && (
                                <button className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium leading-6 text-white whitespace-no-wrap bg-danger border border-red-400 shadow-sm hover:bg-red-400 focus:outline-none focus:shadow-none"
                                        disabled={isDisabled}
                                        onClick={async () => {
                                        let confirm = await TB.alert(
                                            "warning", "Mark Project as Completed",
                                            "Are you sure you want to close and mark this project as completed? <b>This will send out a project satisfaction survey email to all client users and assigned candidates</b>. Note: this project will be archived but can still be access via the Projects dashboard.",
                                            [
                                                {title: 'Yes, Continue'},
                                                {title: 'Cancel'},
                                            ]
                                        )
                                        if (confirm) {
                                            let success = await markProjectAsComplete();

                                            if (success) {
                                                TB.toast("Project completed successfully.", {type: 'success'})
                                                return navigate('/client/projects/all', {replace: true});
                                            }
                                        }
                                    }}
                            >
                                    <CheckSquareOutlined className="text-white text-base"/>
                                    <span>Mark Complete</span>
                            </button>
                            )}
                        </div>

                    </div>
                </div>
            )}

            {isLoading && <LoadingBanner message="Loading Project"/>}

            {TB.isObject(error) && (
                <ErrorBanner {...error}
                             onRetry={refetch}/>
            )}

            {!isLoading && !TB.isObject(error) && (
                <ProjectFormProvider>
                    <Outlet context={{
                        toggleUsability: () => true,
                        queryParams: {},
                        isDisabled: false,
                        goToStep: () => true
                    }}/>
                </ProjectFormProvider>
            )}
        </div>
    )
}

const styles = {
    container: {
        // background: '#323C47',
        alignItems: 'center',
    },
    backBtn: {
        // color: '#F5F6F8',
        alignSelf: 'center',
        flex: 1,
    },
    titleContainer: {
        color: 'white',
        margin: '0 auto',
        // flex: 1,
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
    },
};
export default ProjectDetail;

export {
    ProjectContractHistoryTab,
    ProjectDocumentsTab,
    ProjectOverviewTab,
    ProjectShortlistTab
}